import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, Grid, useTheme } from "@mui/material";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CostProjectFormCst from "../CostProjectFormCst";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import dayjs from "dayjs";

interface ManageCostProjectInterface {
  onCancelAction: () => void;
  onSubmitAction: (data: any, list?: any) => void;
  idCostEdit?: string;
}

const ManageCostProject: React.FC<ManageCostProjectInterface> = ({
  onCancelAction,
  onSubmitAction,
  idCostEdit,
}) => {
  const { t } = useTranslation(["projects-cost-page"]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getProjectsCost}`,
    idCostEdit,
    PagesID["projects.cost-manage"],
  );

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerManageProjectCost } = useSWRMutation(
    `${ServicesURL.postProjectsCost}/${idCostEdit}/action/draft-request`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.POST, { ...arg.arg }),
  );

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerApproveProjectCost } = useSWRMutation(
    `${ServicesURL.postProjectsCost}/${idCostEdit}/action/send-for-approval`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.POST, { ...arg.arg }),
  );

  const onSubmit = async (inputForm: any, sendToApproval: boolean) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    const objToSend: any = {
      request_type: !dataDetail.management_type ? inputForm.request_type : null,
      request_code: inputForm.request_code || null,
      request_date: !!inputForm.request_date
        ? dayjs(new Date(inputForm.request_date)).format("YYYY-MM-DD")
        : null,
      request_notes: inputForm.request_notes || null,
    };

    try {
      if (sendToApproval) await triggerApproveProjectCost();
      else await triggerManageProjectCost(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
    mutate();
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (
        el.accessorKey === "request_type" &&
        dataDetail.management_type &&
        dataDetail.management_type !== "TBD"
      ) {
        return { ...el, disabled: true };
      } else {
        return { ...el };
      }
    });
  }, [dataDetail, columns]);

  return (
    <AnimationFadeIn>
      {columns && (
        <Box
          sx={{
            mt: "25px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: "8px",
              textAlign: "center",
            }}
          >
            {t("hero-title-manage")}
          </Typography>

          <CostProjectFormCst
            row={dataDetail || []}
            columns={getColumns}
            onSubmit={onSubmit}
            backButton={() => {
              onCancelAction();
            }}
            dataProject={dataDetail || []}
            readOnly={
              !(dataDetail.status === "NEW" || dataDetail.status === "DRAFT")
            }
            fullWidth={true}
            isManage={true}
          />
        </Box>
      )}
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        callBackConfirmed={() => {
          setOpenToast(undefined);
          onCancelAction();
          onSubmitAction(undefined, undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default ManageCostProject;
